import React, { useContext } from 'react';
import { useSoyuzEnv } from '@spotahome/soyuz/client';
import PropTypes from 'prop-types';

import SpotahomeTheme from './themes/base';
import ErasmusuTheme from './themes/erasmusu';
import LandlordTheme from './themes/landlord';

import './init.fonts.module.scss';

export const SPOTAHOME_THEME = 'spotahome';
export const ERASMUSU_THEME = 'erasmusu';
export const LANDLORD_THEME = 'landlord';
export const WGGESUCHT_THEME = 'wgg';
export const PARTNER_DEMO_THEME = 'partner_demo';
export const PARTNER_RUFA_THEME = 'partner_rufa';

const THEMES = {
  [SPOTAHOME_THEME]: SpotahomeTheme,
  [ERASMUSU_THEME]: ErasmusuTheme,
  [LANDLORD_THEME]: LandlordTheme,
  // By default, partners will use the same theme as Spotahome
  [WGGESUCHT_THEME]: SpotahomeTheme,
  [PARTNER_RUFA_THEME]: SpotahomeTheme,
  [PARTNER_DEMO_THEME]: SpotahomeTheme
};

export const ThemeContext = React.createContext({
  theme: SPOTAHOME_THEME,
  flags: {
    newButtons: false
  }
});

export const useTheme = () => useContext(ThemeContext);

export const useThemeFlag = flagName => {
  const themeContext = useTheme();

  return themeContext.flags[flagName];
};

const formatCamelCaseToDashes = key =>
  key.replace(/[A-Z]/g, match => `-${match.toLowerCase()}`);

const generateCSSVarSet = (valueSet, prefix) =>
  Object.entries(valueSet)
    .map(
      ([name, value]) =>
        `--${prefix}-${formatCamelCaseToDashes(name)}: ${value};`
    )
    .join('\n');

const ThemeProvider = ({ theme, flags, children }) => {
  const env = useSoyuzEnv();

  const themeName = theme || env.SOYUZ_PUBLIC_BRAND;
  const themeToUse = THEMES[themeName] || SpotahomeTheme;

  return (
    <ThemeContext.Provider value={{ theme: themeName, flags, themeToUse }}>
      <style>
        {`:root {
            ${generateCSSVarSet(themeToUse.Colors, 'sah')}
            ${generateCSSVarSet(themeToUse.Fonts, 'sah-font')}
            ${generateCSSVarSet(themeToUse.LineHeights, 'sah-line-height')}
            ${generateCSSVarSet(themeToUse.FontWeights, 'sah-font-w')}
            ${generateCSSVarSet(themeToUse.ChatButton, 'sah-chat-button')}
          }
          `}
      </style>
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  theme: PropTypes.oneOf(Object.keys(THEMES)),
  // Useful prop for rebranding adoption. We should stop using it as soon as we complete the process
  flags: PropTypes.shape({
    newButtons: PropTypes.bool
  }),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

ThemeProvider.defaultProps = {
  theme: null,
  flags: {
    newButtons: false
  }
};

export default ThemeProvider;
